class PurchaseOrderItem {
  constructor(data = {}) {
    this.id = data.id;
    this.purchase_order_id = data.purchase_order_id;
    this.product_id = data.product_id;
    this.quantity_ordered = data.quantity_ordered || 0;
    this.unit_cost = data.unit_cost || 0;
    this.line_total = data.line_total || 0;
    this.received_total = data.received_total || 0;
    this.quantity_received = data.quantity_received || 0;
    this.product = data.product;
    
    // Receiving history fields
    this.receiving_history = data.receiving_history || [];
    this.quantity_received_total = data.quantity_received_total || 0;
    this.variance = data.variance !== undefined ? data.variance : null;
  }

  calculateLineTotal() {
    this.line_total = Number((this.quantity_ordered * this.unit_cost).toFixed(2));
    return this.line_total;
  }
}

class PurchaseOrder {
  constructor(data = {}) {
    this.id = data.id;
    this.po_number = data.po_number;
    this.supplier_id = data.supplier_id;
    this.status = data.status || 'DRAFT';
    this.order_date = data.order_date || new Date().toISOString().split('T')[0];
    this.notes = data.notes || '';
    this.total_amount = data.total_amount || 0;
    this.total_received_amount = data.total_received_amount || 0;
    this.created_by_user_id = data.created_by_user_id;
    this.supplier = data.supplier;
    this.created_by = data.created_by;
    this.created_at = data.created_at;
    this.updated_at = data.updated_at;
    this.available_transitions = data.available_transitions || [];

    // Historical PO flag - when true, receiving items won't affect inventory
    this.is_historical = data.is_historical || false;

    // Financial fields
    this.financial_status = data.financial_status || 'UNPAID';
    this.bank_transaction_id = data.bank_transaction_id || null;
    this.paid_at = data.paid_at || null;
    this.paid_by_account_id = data.paid_by_account_id || null;
    this.bank_transaction = data.bank_transaction || null;
    this.paid_by = data.paid_by || null;
    this.payment_reference_code = data.payment_reference_code || null;

    // Handle items array
    this.items = [];
    if (Array.isArray(data.items)) {
      this.items = data.items.map(item => new PurchaseOrderItem(item));
    }
  }

  mapData(data) {
    this.id = data.id;
    this.po_number = data.po_number;
    this.supplier_id = data.supplier_id;
    this.status = data.status || 'DRAFT';
    this.order_date = data.order_date;
    this.notes = data.notes;
    this.total_amount = data.total_amount;
    this.total_received_amount = data.total_received_amount || 0;
    this.created_by_user_id = data.created_by_user_id;
    this.supplier = data.supplier;
    this.created_by = data.created_by;
    this.created_at = data.created_at;
    this.updated_at = data.updated_at;
    this.available_transitions = data.available_transitions || [];

    // Historical PO flag
    this.is_historical = data.is_historical || false;

    // Financial fields
    this.financial_status = data.financial_status || 'UNPAID';
    this.bank_transaction_id = data.bank_transaction_id || null;
    this.paid_at = data.paid_at || null;
    this.paid_by_account_id = data.paid_by_account_id || null;
    this.bank_transaction = data.bank_transaction || null;
    this.paid_by = data.paid_by || null;
    this.payment_reference_code = data.payment_reference_code || null;

    // Handle items array
    this.items = [];
    if (Array.isArray(data.items)) {
      this.items = data.items.map(item => new PurchaseOrderItem(item));
    }
  }

  addItem(productId = null, quantity = 1, unitCost = 0) {
    const newItem = new PurchaseOrderItem({
      product_id: productId,
      quantity_ordered: quantity,
      unit_cost: unitCost,
      quantity_received: 0
    });
    newItem.calculateLineTotal();
    this.items.push(newItem);
    this.calculateTotal();
    return newItem;
  }

  removeItem(index) {
    if (index >= 0 && index < this.items.length) {
      this.items.splice(index, 1);
      this.calculateTotal();
    }
  }

  calculateTotal() {
    this.total_amount = Number(
      this.items.reduce((sum, item) => sum + item.calculateLineTotal(), 0).toFixed(2)
    );
    return this.total_amount;
  }

  // Convert to API format
  toApiFormat() {
    return {
      supplier_id: this.supplier_id,
      order_date: this.order_date,
      notes: this.notes,
      is_historical: this.is_historical,
      items: this.items.map(item => ({
        product_id: item.product_id,
        quantity_ordered: Number(item.quantity_ordered),
        unit_cost: Number(item.unit_cost)
      }))
    };
  }

  // Validation
  isValid() {
    if (!this.supplier_id) return false;
    if (!this.order_date) return false;
    if (this.items.length === 0) return false;
    
    return this.items.every(item => 
      item.product_id && 
      item.quantity_ordered > 0 && 
      item.unit_cost >= 0
    );
  }

  getValidationErrors() {
    const errors = {};
    
    if (!this.supplier_id) {
      errors.supplier_id = 'Vui lòng chọn nhà cung cấp';
    }
    
    if (!this.order_date) {
      errors.order_date = 'Vui lòng chọn ngày đặt hàng';
    }
    
    if (this.items.length === 0) {
      errors.items = 'Cần ít nhất một sản phẩm';
    } else {
      const itemErrors = [];
      this.items.forEach((item, index) => {
        const itemError = {};
        
        if (!item.product_id) {
          itemError.product_id = 'Vui lòng chọn sản phẩm';
        }
        
        if (!item.quantity_ordered || item.quantity_ordered <= 0) {
          itemError.quantity_ordered = 'Số lượng phải lớn hơn 0';
        }
        
        if (item.unit_cost < 0) {
          itemError.unit_cost = 'Đơn giá không được âm';
        }
        
        if (Object.keys(itemError).length > 0) {
          itemErrors[index] = itemError;
        }
      });
      
      if (Object.keys(itemErrors).length > 0) {
        errors.items = itemErrors;
      }
    }
    
    return errors;
  }
}

export default PurchaseOrder;
export { PurchaseOrderItem };
